// Waitlist & Access — console-standard. Merges the waitlist queue (invite / bulk
// invite / remove / export) with beta access-code management (formerly Beta Access).
import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, KPI, Panel, SectionLabel, Table, Pill, Empty, ExportButton, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = n => (n == null ? '…' : Number(n).toLocaleString());
function genCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function Body() {
  const { t } = useConsoleTheme();
  const mobile = useIsMobile();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [me, setMe] = useState('admin');
  // access code
  const [config, setConfig] = useState(null);
  const [newCode, setNewCode] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const [codeMsg, setCodeMsg] = useState('');

  function showToast(msg, type) { setToast({ msg, type: type || 'ok' }); setTimeout(() => setToast(null), 3000); }

  const loadList = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_waitlist');
      if (error) { console.error('Failed to load waitlist:', error); setList([]); }
      else setList(data || []);
    } catch (e) { console.error('Error loading waitlist:', e); setList([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadList();
    supabase.auth.getUser().then(({ data }) => { if (data?.user) setMe(data.user.email?.split('@')[0] || 'admin'); });
    supabase.from('app_config').select('*').limit(20).then(({ data }) => setConfig(data || []));
  }, [loadList]);

  async function handleInvite(id, email) {
    setInviting(s => new Set([...s, id]));
    try {
      await supabase.rpc('admin_waitlist_invite', { p_id: id });
      setList(l => l.map(r => r.id === id ? { ...r, status: 'invited', invited_at: new Date().toISOString() } : r));
      showToast('Invite sent to ' + email);
      try { await supabase.rpc('admin_log_action', { p_actor: me, p_actor_email: '', p_action_type: 'waitlist_invited', p_subject: email, p_detail: 'Beta invite sent' }); } catch (_e) {}
    } catch (_e) { showToast('Failed to invite ' + email, 'warn'); }
    setInviting(s => { const n = new Set(s); n.delete(id); return n; });
  }

  async function handleBulkInvite() {
    const waiting = list.filter(r => r.status === 'waiting').slice(0, 10);
    if (waiting.length === 0) return;
    if (!window.confirm(`Send invites to ${waiting.length} people?\n\n${waiting.map(r => r.email).join('\n')}`)) return;
    await Promise.allSettled(waiting.map(r => handleInvite(r.id, r.email)));
    showToast(`Bulk invite complete — ${waiting.length} sent`);
  }

  async function handleRemove(id, email) {
    try {
      await supabase.rpc('admin_waitlist_delete', { p_id: id });
      setList(l => l.filter(r => r.id !== id));
      showToast('Removed ' + email, 'warn');
      try { await supabase.rpc('admin_log_action', { p_actor: me, p_actor_email: '', p_action_type: 'waitlist_removed', p_subject: email, p_detail: 'Removed from waitlist' }); } catch (_e) {}
    } catch (_e) { showToast('Failed to remove', 'warn'); }
  }

  async function saveCode() {
    if (!newCode.trim()) return;
    setSavingCode(true);
    try {
      const { error } = await supabase.from('app_config').upsert({ key: 'beta_code', value: newCode.trim() }, { onConflict: 'key' });
      if (!error) {
        setConfig(prev => { const next = (prev || []).filter(r => r.key !== 'beta_code'); return [...next, { key: 'beta_code', value: newCode.trim() }]; });
        setCodeMsg('Beta code updated in app_config. The app reads REACT_APP_BETA_CODE by default — this sets the code for any future build that reads from app_config.');
        setNewCode('');
      }
    } catch (_e) {} finally { setSavingCode(false); }
  }

  const waiting = list.filter(r => r.status === 'waiting').length;
  const invited = list.filter(r => r.status === 'invited').length;
  const sources = list.reduce((a, r) => { a[r.source] = (a[r.source] || 0) + 1; return a; }, {});
  const topSrc = Object.entries(sources).sort((a, b) => b[1] - a[1])[0];
  const currentCode = config?.find(r => r.key === 'beta_code')?.value || '2314 (env default)';

  const cols = [
    { label:'Email', key:'email', render:r => r.email },
    { label:'Source', key:'source', muted:true, render:r => r.source || '—' },
    { label:'Country', key:'country', muted:true, render:r => r.country || '—' },
    { label:'Joined', muted:true, sort:r => r.created_at ? +new Date(r.created_at) : 0,
      text:r => r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '',
      render:r => r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : '—' },
    { label:'Status', text:r => r.status, render:r => <Pill tone={r.status === 'invited' ? 'up' : 'warn'}>{r.status === 'invited' ? 'Invited' : 'Waiting'}</Pill> },
    { label:'Action', align:'right', render:r => (
        <span style={{ display:'inline-flex', gap:6, justifyContent:'flex-end' }}>
          {r.status === 'waiting'
            ? <button onClick={e => { e.stopPropagation(); handleInvite(r.id, r.email); }} disabled={inviting.has(r.id)}
                style={{ padding:'3px 10px', background:t.up, color:'#06222e', border:'none', borderRadius:6, fontSize:'0.82rem', fontWeight:600, cursor:inviting.has(r.id)?'default':'pointer', fontFamily:F }}>{inviting.has(r.id) ? '…' : 'Invite'}</button>
            : <span style={{ fontSize:'0.82rem', color:t.up, padding:'3px 9px' }}>✓ Sent</span>}
          <button onClick={e => { e.stopPropagation(); handleRemove(r.id, r.email); }}
            style={{ padding:'3px 9px', background:t.danger+'22', color:t.danger, border:'none', borderRadius:6, fontSize:'0.82rem', cursor:'pointer', fontFamily:F }}>×</button>
        </span>
      ) },
  ];

  return (
    <div>
      {toast && <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', background: toast.type === 'warn' ? t.warn : t.up, color:'#06222e', padding:'10px 20px', borderRadius:10, fontSize:'0.92rem', fontWeight:600, zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.3)' }}>{toast.msg}</div>}

      <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:10 }}>
        <KPI label="Total signups" value={loading ? '…' : fmt(list.length)} sub="on waitlist" accent="#9F7E4F" />
        <KPI label="Waiting" value={loading ? '…' : fmt(waiting)} sub="to be invited" subColor={waiting > 0 ? t.warn : t.mut} accent="#d97706" />
        <KPI label="Invited" value={loading ? '…' : fmt(invited)} sub="sent access" subColor={t.up} accent="#17a86a" />
        <KPI label="Top source" value={loading ? '…' : (topSrc ? topSrc[0] : '—')} sub={topSrc ? `${topSrc[1]} signups` : 'none yet'} accent={t.accent} />
      </div>

      <SectionLabel>Beta access code</SectionLabel>
      <Panel title="◈ Access code">
        <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '220px 1fr', gap:16, alignItems:'center' }}>
          <div style={{ background:t.codebg, borderRadius:8, padding:'14px 16px', fontFamily:'monospace', fontSize:'1.5rem', letterSpacing:'.15em', color:t.accent, textAlign:'center' }}>{config == null ? '…' : currentCode}</div>
          <div>
            <div style={{ display:'flex', gap:8 }}>
              <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())} placeholder="New code (e.g. SANTEST1)"
                style={{ flex:1, padding:'8px 12px', borderRadius:8, border:`1px solid ${t.bd}`, background:t.rowbg, color:t.tx, fontSize:'0.95rem', fontFamily:'monospace', letterSpacing:'.08em', outline:'none' }} />
              <button onClick={() => setNewCode(genCode())} style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${t.bd}`, background:t.period, color:t.mut, fontSize:'0.85rem', cursor:'pointer', fontFamily:F }}>Gen</button>
              <button onClick={saveCode} disabled={savingCode || !newCode.trim()} style={{ padding:'8px 16px', borderRadius:8, background:t.accent, color: t.bg === '#f2f1ef' ? '#fff' : '#06222e', border:'none', fontSize:'0.85rem', fontWeight:600, cursor: savingCode || !newCode.trim() ? 'default' : 'pointer', fontFamily:F, opacity: savingCode || !newCode.trim() ? 0.7 : 1 }}>{savingCode ? 'Saving…' : 'Save'}</button>
            </div>
            {codeMsg && <div style={{ marginTop:10 }}><Empty>{codeMsg}</Empty></div>}
            <div style={{ fontSize:'0.82rem', color:t.mut, marginTop:10 }}>Beta URL: <a href="https://www.sanoire.com" target="_blank" rel="noreferrer" style={{ color:t.search }}>sanoire.com</a></div>
          </div>
        </div>
      </Panel>

      <SectionLabel right={<span style={{ display:'inline-flex', gap:6 }}>
        {waiting > 0 && <button onClick={handleBulkInvite} style={{ padding:'5px 11px', background:t.up, color:'#06222e', border:'none', borderRadius:7, fontSize:'0.85rem', fontWeight:600, cursor:'pointer', fontFamily:F }}>Invite top 10</button>}
        <ExportButton filename="sanoire-waitlist" cols={cols} rows={list} />
      </span>}>Waitlist queue · {waiting} waiting</SectionLabel>
      <Panel>
        {loading ? <Empty>Loading waitlist…</Empty>
          : list.length === 0 ? <Empty>No signups yet. Once the waitlist form is live on sanoire.com, entries appear here.</Empty>
          : <Table cols={cols} rows={list} pageSize={20} searchable />}
      </Panel>
    </div>
  );
}

export default function Waitlist(props) {
  if (props && props.embed) return <Body />;
  return <ConsoleShell title="Waitlist & Access"><Body /></ConsoleShell>;
}
